import { maxBy, minBy } from 'lodash';

import dayjs from './dayjs';

export const dateFormatForUI = 'Do MMMM YYYY';
export const compactDateFormatForUI = 'Do MMM YYYY';
export const dateFormatForMobileUI = 'D MMM YY';
export const timeFormatForUI = 'HH:mm';
export const dateFormatForAPI = 'YYYY-MM-DD';
export const shortDateFormat = 'DD/MM/YY';
export const dateFormatForSearchUI = 'Do MMM YY';

// dayjs treats a lowercase `y` as a literal, so use the uppercase year token
// (moment accepted `yyyy`; dayjs needs `YYYY`).
export const defaultDateFormat = 'DD/MM/YYYY';

export const generateTimeRange = (
  start?: string | null,
  end?: string | null,
  isEnd = false
) => {
  if (!start || !end) return [];

  // extract hours and minutes integers from start and end time
  const [startHour, startMinute] = start.split(':');
  const [endHour, endMinute] = end.split(':');

  // create some timestamps with those times, we don't care what the date is
  const startTimestamp = dayjs()
    .hour(Number(startHour))
    .minute(Number(startMinute))
    .second(0)
    .millisecond(0);
  const endTimestamp = dayjs()
    .hour(Number(endHour))
    .minute(Number(endMinute))
    .second(0)
    .millisecond(0);

  // depending on if this is the start or end field, iterate over the range in
  // 30-minute steps (inclusive of both ends), replacing moment-range's `.by()`.
  const rangeStart = isEnd ? startTimestamp.add(1, 'hour') : startTimestamp;

  const times: string[] = [];
  let cursor = rangeStart;
  while (!cursor.isAfter(endTimestamp)) {
    times.push(cursor.format('HH:mm'));
    cursor = cursor.add(30, 'minute');
  }

  return times;
};

export const generateTimeList = (
  start?: string | null,
  end?: string | null,
  isEnd = false
) => {
  if (!start || !end) return [];

  const timeRange = generateTimeRange(start, end, isEnd);

  // generate array of select values and return it
  return timeRange.map(t => ({
    value: t,
    label: t,
  }));
};

export const getStartAndEndTimes = (
  openingTime: string,
  closingTime: string,
  date: dayjs.ConfigType = undefined
) => {
  return {
    startTime: dayjs(date).isSame(dayjs(), 'day')
      ? dayjs().startOf('hour').format('HH:mm')
      : openingTime,
    endTime: closingTime,
  };
};

export const formatDate = (
  date?: dayjs.ConfigType,
  format = defaultDateFormat
) => dayjs(date).format(format);

// A value `new Date(...)`/dayjs can read a date out of. Narrower than
// `dayjs.ConfigType`, which also permits null/undefined.
type DateValue = string | number | Date;

// Comparators and date pickers below are keyed by a property name rather than
// taking an accessor, so they are generic over that key: passing 'startTime'
// constrains the items to objects that actually have a date-like `startTime`.
export const sortByTime = <K extends string>(key: K) => {
  return (a: Record<K, DateValue>, b: Record<K, DateValue>) => {
    if (dayjs(a[key]) < dayjs(b[key])) {
      return -1;
    }

    if (dayjs(a[key]) === dayjs(b[key])) {
      return 0;
    }

    return 1;
  };
};

export const dateInCurrentMonth = (date: dayjs.ConfigType) =>
  dayjs.utc(date).isSame(dayjs.utc(), 'month');

export const maxDate = <K extends string>(
  items: Record<K, DateValue>[],
  key: K
) => {
  if (!items.length) {
    return undefined;
  }

  return new Date(
    maxBy(items, item => {
      return dayjs.utc(item[key]);
    })![key]
  );
};

export const minDate = <K extends string>(
  items: Record<K, DateValue>[],
  key: K
) => {
  if (!items.length) {
    return undefined;
  }

  return new Date(
    minBy(items, item => {
      return dayjs.utc(item[key]);
    })![key]
  );
};

export const midDate = <K extends string>(
  items: Record<K, DateValue>[],
  key: K
) => {
  if (!items.length) {
    return undefined;
  }

  const getTimeFromDate = (date: dayjs.ConfigType) =>
    dayjs.utc(date).toDate().getTime();

  return new Date(
    (getTimeFromDate(maxDate(items, key)) +
      getTimeFromDate(minDate(items, key))) /
      2
  );
};

export const isPastDate = (date: string) =>
  dayjs(date).endOf('day').isBefore(dayjs());

export const isThisMonth = (date: string) =>
  dayjs(date).isBefore(dayjs().endOf('month'));
